import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import './TiptapEditor.css';

interface TiptapEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

const HEADING_OPTIONS = [
  { label: 'Paragraph', value: 'paragraph' },
  { label: 'Heading 1', value: 'h1' },
  { label: 'Heading 2', value: 'h2' },
  { label: 'Heading 3', value: 'h3' },
];

const TiptapEditor: React.FC<TiptapEditorProps> = ({
  value = '',
  onChange,
  placeholder = 'Nhập nội dung...',
  minHeight = 320,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
      TextStyle,
      Color,
    ],
    content: value,
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML();
      onChange?.(html === '<p></p>' ? '' : html);
    },
  });

  // Sync external value changes (e.g. when form loads data)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    if (current !== incoming) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const headingValue = editor.isActive('heading', { level: 1 })
    ? 'h1'
    : editor.isActive('heading', { level: 2 })
    ? 'h2'
    : editor.isActive('heading', { level: 3 })
    ? 'h3'
    : 'paragraph';

  const handleHeadingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === 'paragraph') editor.chain().focus().setParagraph().run();
    else if (val === 'h1') editor.chain().focus().toggleHeading({ level: 1 }).run();
    else if (val === 'h2') editor.chain().focus().toggleHeading({ level: 2 }).run();
    else if (val === 'h3') editor.chain().focus().toggleHeading({ level: 3 }).run();
  };

  const handleLinkClick = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt('Nhập URL:', 'https://');
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <div className="tiptap-wrapper">
      <div className="tiptap-toolbar">
        {/* Heading */}
        <div className="tiptap-toolbar-group">
          <select className="tiptap-select" value={headingValue} onChange={handleHeadingChange}>
            {HEADING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="tiptap-toolbar-divider" />

        {/* Inline styles */}
        <div className="tiptap-toolbar-group">
          <button
            type="button"
            className={`tiptap-btn${editor.isActive('bold') ? ' is-active' : ''}`}
            onClick={() => editor.chain().focus().toggleBold().run()}
            title="Bold"
          >
            <b>B</b>
          </button>
          <button
            type="button"
            className={`tiptap-btn${editor.isActive('italic') ? ' is-active' : ''}`}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            title="Italic"
          >
            <i>I</i>
          </button>
          <button
            type="button"
            className={`tiptap-btn${editor.isActive('underline') ? ' is-active' : ''}`}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            title="Underline"
          >
            <u>U</u>
          </button>
          <button
            type="button"
            className={`tiptap-btn${editor.isActive('strike') ? ' is-active' : ''}`}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            title="Strikethrough"
          >
            <s>S</s>
          </button>
        </div>

        <div className="tiptap-toolbar-divider" />

        {/* Text align */}
        <div className="tiptap-toolbar-group">
          {(['left', 'center', 'right'] as const).map((align) => (
            <button
              key={align}
              type="button"
              className={`tiptap-btn${editor.isActive({ textAlign: align }) ? ' is-active' : ''}`}
              onClick={() => editor.chain().focus().setTextAlign(align).run()}
              title={`Align ${align}`}
            >
              {align === 'left' ? '≡' : align === 'center' ? '≡' : '≡'}
              <span style={{ fontSize: 10, marginLeft: 1 }}>
                {align === 'left' ? 'L' : align === 'center' ? 'C' : 'R'}
              </span>
            </button>
          ))}
        </div>

        <div className="tiptap-toolbar-divider" />

        {/* Lists */}
        <div className="tiptap-toolbar-group">
          <button
            type="button"
            className={`tiptap-btn${editor.isActive('bulletList') ? ' is-active' : ''}`}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            title="Bullet list"
          >
            •≡
          </button>
          <button
            type="button"
            className={`tiptap-btn${editor.isActive('orderedList') ? ' is-active' : ''}`}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            title="Ordered list"
          >
            1≡
          </button>
          <button
            type="button"
            className={`tiptap-btn${editor.isActive('blockquote') ? ' is-active' : ''}`}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            title="Blockquote"
          >
            "
          </button>
        </div>

        <div className="tiptap-toolbar-divider" />

        {/* Link */}
        <div className="tiptap-toolbar-group">
          <button
            type="button"
            className={`tiptap-btn${editor.isActive('link') ? ' is-active' : ''}`}
            onClick={handleLinkClick}
            title="Link"
          >
            🔗
          </button>
        </div>

        <div className="tiptap-toolbar-divider" />

        {/* Color */}
        <div className="tiptap-toolbar-group">
          <input
            type="color"
            title="Text color"
            style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', padding: 2, borderRadius: 4 }}
            onInput={(e) => editor.chain().focus().setColor((e.target as HTMLInputElement).value).run()}
          />
        </div>

        <div className="tiptap-toolbar-divider" />

        {/* History */}
        <div className="tiptap-toolbar-group">
          <button
            type="button"
            className="tiptap-btn"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo"
          >
            ↩
          </button>
          <button
            type="button"
            className="tiptap-btn"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo"
          >
            ↪
          </button>
        </div>
      </div>

      <div className="tiptap-content" style={{ minHeight }}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default TiptapEditor;
